import { Injectable } from '@angular/core';
import { BehaviorSubject, from } from 'rxjs';
import { LevantamientoSeleccionadoDTO } from '../models/dtos/levantamiento-seleccionado-dto';
import { take, concatMap, map, toArray } from 'rxjs/operators';
import { Capa } from '../models/entities/capa';
import { Segmento } from '../models/entities/segmento';
import { CapaSegmentosDTO } from '../models/dtos/capa-segmentos-dto';
import { ElementoCatalogo } from '../models/entities/elemento-catalogo';
import { ArchivoCoordenada } from '../models/entities/archivo-coordenada';
import { Coordenada } from '../models/entities/coordenada';
import { SegmentoService } from './segmento.service';
import { CapaService } from './capa.service';
import { ArchivoCoordenadaService } from './archivo-coordenada.service';
import { CatalogoService } from './catalogo.service';
import { RutaCatalogoEnum } from '../shared/enums/ruta-catalogo.enum';
import { MapsAPILoader } from '@agm/core';
import { AvanceDTO } from '../models/dtos/avance-dto';

@Injectable({
  providedIn: 'root'
})
export class LevantamientoAvanceService {

  public readonly CAPA_PROYECTO_PROCESO: string = '0';
  public readonly CAPA_PROYECTO_PLANEACION: string = '4';
  public readonly CAPA_META_ACTUAL: string = '1';
  public readonly CAPA_COMPLETADO: string = '2';
  public readonly CAPA_ESTRUCTURA: string = '5';
  public catColores: ElementoCatalogo[] = [];
  public catCapas: ElementoCatalogo[] = [];

  /**
   * Este behaviorsubject se encarga de tener el estado actual de la obra con todas sus capas.
   *  Cada que se agregue una capa se debe actualzar este objeto
   */
  public datosObraSeleccionada$: BehaviorSubject<LevantamientoSeleccionadoDTO> = new BehaviorSubject<LevantamientoSeleccionadoDTO>(null);
  constructor(
    private segmentoService: SegmentoService,
    private capaService: CapaService,
    private archivoCoordenadaService: ArchivoCoordenadaService,
    private catalogoService: CatalogoService,
    private mapsAPILoader: MapsAPILoader
  ) {
    this.catalogoService.obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.COLORES).pipe(take(1)).toPromise().then(
      (colores: ElementoCatalogo[]) => this.catColores = colores
    );
    this.catalogoService.obtenerCatalogo<ElementoCatalogo>(RutaCatalogoEnum.CAPAS).pipe(take(1)).toPromise().then(
      (capas: ElementoCatalogo[]) => this.catCapas = capas
    );
  }


  /**
   * Actualiza el behaviorsubject del servicio levantamientoAvance
   */
  public async actualizarObra(datosObra: LevantamientoSeleccionadoDTO): Promise<void> {
    await this.mapsAPILoader.load();
    let obraConNuevaInfo: LevantamientoSeleccionadoDTO = await this.capaService.findByKeyLevantamientoObra(datosObra.levObra.key)
      .pipe(
        take(1),
        concatMap(
          (capas: Capa[]) => from(capas)
            .pipe(
              concatMap(
                (capa: Capa) => this.segmentoService.findByKeyCapa(capa.key)
                  .pipe(
                    take(1),
                    map(
                      (segmentos: Segmento[]) => {
                        let longitudSegmentos: number = 0;
                        segmentos.forEach((segmento: Segmento) => {
                          let path: Array<google.maps.LatLng> = Array.from(segmento.coordenadas, (c: Coordenada) => new google.maps.LatLng(c.latitud, c.longitud));
                          let longitudSegmento: number = google.maps.geometry.spherical.computeLength(path);
                          longitudSegmentos += parseFloat(longitudSegmento.toFixed(2));
                        });
                        let elementoCoordenadaDTO: CapaSegmentosDTO = {
                          capa,
                          segmentos: segmentos.map(
                            (segmento: Segmento) => (
                              { ...segmento, nombreColor: this.catColores.find((e: ElementoCatalogo) => e.key === segmento.keyCatColor).nombre }
                            )
                          ),
                          nombreCapa: this.catCapas.find((e: ElementoCatalogo) => e.key === capa.keyCatCapa).nombre,
                          longitudSegmentos
                        }
                        return elementoCoordenadaDTO;
                      }
                    )
                  )
              ),
              toArray()
            )
        ),
        map(
          (capasSegmentos: CapaSegmentosDTO[]) => (
            {
              ...datosObra,
              capasSegmentos
            } as LevantamientoSeleccionadoDTO
          )
        ),
        concatMap(
          (datosObraSeleccionada: LevantamientoSeleccionadoDTO) => this.archivoCoordenadaService.findByKeyLevantamientoObra(datosObraSeleccionada.levObra.key).pipe(
            take(1),
            map(
              (archivos: ArchivoCoordenada[]) => {
                datosObraSeleccionada.capasSegmentos.forEach(
                  (capaSegmentos: CapaSegmentosDTO) => {
                    capaSegmentos.segmentos.forEach(
                      (segmento: Segmento) => {
                        segmento.coordenadas.forEach(
                          (coordenada: Coordenada, indice: number) => {
                            let archivosEncontrados: ArchivoCoordenada[] = archivos.filter((a: ArchivoCoordenada) => a.keySegmento === segmento.key && a.keyCoordenada === indice.toString());
                            if (archivosEncontrados.length > 0) {
                              coordenada.archivos = archivosEncontrados;
                            } else {
                              coordenada.archivos = [];
                            }
                          }
                        )
                      }
                    );
                    capaSegmentos.mostrarCapa = true;
                    switch (capaSegmentos.capa.keyCatCapa) {
                      case this.CAPA_PROYECTO_PROCESO: capaSegmentos.zIndex = 10; break;
                      case this.CAPA_PROYECTO_PLANEACION: capaSegmentos.zIndex = 10; break;
                      case this.CAPA_META_ACTUAL: capaSegmentos.zIndex = 20; break;
                      case this.CAPA_COMPLETADO: capaSegmentos.zIndex = 30; break;
                      case this.CAPA_ESTRUCTURA: capaSegmentos.zIndex = 40; break;
                    }
                  }
                )
                // Se crea el arreglo de avances. Cada avance tiene su propia fecha y un arreglo de segmentos. Se agrupan segmentos por fecha
                //  Ademas, ese arreglo de segmentos tiene un calculo con la suma de todas sus longitudes.
                let capaCompletado: CapaSegmentosDTO = datosObraSeleccionada.capasSegmentos.find((capaSegmentos: CapaSegmentosDTO) => capaSegmentos.capa.keyCatCapa === this.CAPA_COMPLETADO);
                let avances: AvanceDTO[] = [];
                if (capaCompletado) {
                  let mapaFechas = new Map<number, Segmento[]>();
                  capaCompletado.segmentos.forEach((segmento: Segmento) => {
                    let segmentos: Segmento[] = mapaFechas.get(segmento.fechaRegistro ? segmento.fechaRegistro : segmento.fechaCreacion);
                    if (!segmentos) {
                      segmentos = [];
                    }
                    segmentos.push(segmento);
                    mapaFechas.set(segmento.fechaRegistro ? segmento.fechaRegistro : segmento.fechaCreacion, segmentos);
                  });

                  mapaFechas.forEach((segmentos: Segmento[], fecha: number) => {
                    let longitudSegmentos: number = 0;
                    segmentos.forEach((segmento: Segmento) => {
                      let path: Array<google.maps.LatLng> = Array.from(segmento.coordenadas, (c: Coordenada) => new google.maps.LatLng(c.latitud, c.longitud));
                      let longitudSegmento: number = google.maps.geometry.spherical.computeLength(path);
                      longitudSegmentos += parseFloat(longitudSegmento.toFixed(2));
                    });

                    let avance: AvanceDTO = {
                      capa: capaCompletado.capa,
                      nombreCapa: capaCompletado.nombreCapa,
                      zIndex: capaCompletado.zIndex,
                      mostrarCapa: capaCompletado.mostrarCapa,
                      fecha,
                      segmentos,
                      longitudSegmentos
                    };

                    avances.push(avance);
                  });
                }
                datosObraSeleccionada.avances = avances;
                return datosObraSeleccionada;
              }
            )
          )
        ),
        take(1)
      ).toPromise();
    this.datosObraSeleccionada$.next(obraConNuevaInfo);
  }

}
